import { ClickAwayListener } from '@mui/material';
import React, { MouseEventHandler } from 'react';
import { DraggableData, DraggableEvent } from 'react-draggable';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import {
  SIGNATURE_COMPONENT_DEFAULT_HEIGHT,
  SIGNATURE_COMPONENT_DEFAULT_WIDTH,
} from 'src/constants';
import { RootState } from 'src/store/reduxTypes';
import {
  RemoveComponentAction,
  RemoveSelectedComponent,
  SetSelectedComponent,
  UpdateComponentAction,
  UpdateComponentDimensionsAction,
} from 'src/store/signaturePage/actions';
import { SignaturePageComponent } from 'src/store/signaturePage/types';

import {
  ContractDraggableItem,
  ContractDragItemType,
} from './ContractDraggableItem';

export interface ISize {
  height: number;
  width: number;
}
export interface SignatureDraggableComponentProps {
  component: SignaturePageComponent;
  requestComponentClicked: (component: SignaturePageComponent) => void;
  setElementReference: (element: HTMLButtonElement | null) => void;
  refreshElementsReference: boolean;
}

const SignatureDraggableComponentBase: React.FC<
  SignatureDraggableComponentProps
> = ({
  component,
  requestComponentClicked,
  setElementReference,
  refreshElementsReference,
}) => {
  const dispatch = useDispatch();
  const draggableButtonRef = React.useRef<HTMLButtonElement>(null);

  const { isClient, startedEsignFlow, isSignatureButtonClickAwayDisabled } =
    useSelector(
      (state: RootState) => ({
        isClient: state.user.isClient,
        startedEsignFlow: state.signaturePage.startedEsignFlow,
        isSignatureButtonClickAwayDisabled:
          state.signaturePage.isSignatureButtonClickAwayDisabled,
      }),
      shallowEqual,
    );

  const isComponentSelected = useSelector(
    (state: RootState) =>
      state.signaturePage.selectedComponentKey === component.key,
  );

  /**
   * Handle draggable component being moved. This will update
   * the store for signatures (not files store)
   * @param e the draggable event
   * @param data the data from draggable
   */
  const handleComponentMoved = React.useCallback(
    (_: DraggableEvent, data: DraggableData) => {
      // Update signature component coords only when it's moved from the last position
      if (component.xPosition !== data.x || component.yPosition !== data.y) {
        const updatedCompnent = {
          ...component,
          xPosition: data.x,
          yPosition: data.y,
        };
        dispatch(UpdateComponentAction(updatedCompnent, data));
        // on drag stop make component selected
        if (component?.key) dispatch(SetSelectedComponent(component?.key));
      }
    },
    [component, dispatch],
  );

  /**
   * Handle signature component being resized. This will update
   * the store for signatures (not files store)
   * @param dimensions the dimensions from resizable i.e height and width of the component
   */
  const handleComponentResized = React.useCallback(
    (dimensions: ISize, componentId: string) => {
      if (component.key !== componentId) return;

      if (
        component.height !== dimensions.height ||
        component.width !== dimensions.width
      ) {
        const updatedCompnent = {
          ...component,
          ...dimensions,
        };
        dispatch(UpdateComponentDimensionsAction(updatedCompnent));
      }
    },
    [component, dispatch],
  );

  const handleDeSelectedComponent = React.useCallback(() => {
    dispatch(RemoveSelectedComponent());
  }, [dispatch]);

  const handleComponentClicked: MouseEventHandler<HTMLButtonElement> =
    React.useCallback(
      (e) => {
        e.stopPropagation();
        if (!isClient) {
          //  make this component selected
          dispatch(SetSelectedComponent(component.key));
        }
        if (isClient && component.componentType.startsWith('request')) {
          // if this is a request component then trigger action based on what is being requested
          if (!startedEsignFlow) {
            // if user has not clicked to start flow then dont do anything or it is the 0 step
            return;
          }

          requestComponentClicked(component);
        }
      },
      [
        component,
        dispatch,
        isClient,
        requestComponentClicked,
        startedEsignFlow,
      ],
    );

  const handleRemoveComponent = React.useCallback(() => {
    dispatch(RemoveComponentAction(component.key));
  }, [component.key, dispatch]);

  const handleComponentClickAway = React.useCallback(() => {
    if (isClient) return;

    if (isSignatureButtonClickAwayDisabled) return;

    if (isComponentSelected) {
      // when internal user clicks away from component, make it not selected
      handleDeSelectedComponent();
    }
  }, [
    handleDeSelectedComponent,
    isClient,
    isComponentSelected,
    isSignatureButtonClickAwayDisabled,
  ]);

  React.useEffect(() => {
    setElementReference(draggableButtonRef.current);
  }, [setElementReference]);

  React.useEffect(() => {
    if (refreshElementsReference) {
      setElementReference(draggableButtonRef.current);
    }
  }, [refreshElementsReference, setElementReference]);

  const stageDraggableComponent = React.useMemo(() => {
    const stagedComponent = { ...component };
    // If component width and height is not available fallback to default width and height
    if (stagedComponent) {
      if (!stagedComponent.width) {
        stagedComponent.width = SIGNATURE_COMPONENT_DEFAULT_WIDTH;
      }

      if (!stagedComponent?.height) {
        stagedComponent.height = SIGNATURE_COMPONENT_DEFAULT_HEIGHT;
      }
    }
    return stagedComponent;
  }, [component]);

  return (
    <ClickAwayListener onClickAway={handleComponentClickAway}>
      <ContractDraggableItem
        type={ContractDragItemType.Edit}
        ref={draggableButtonRef}
        component={stageDraggableComponent}
        handleComponentClicked={handleComponentClicked}
        handleComponentMoved={handleComponentMoved}
        handleComponentResized={handleComponentResized}
        handleRemoveComponent={handleRemoveComponent}
        isComponentSelected={isComponentSelected}
        disableDragging={isClient}
      />
    </ClickAwayListener>
  );
};

export const SignatureDraggableComponent = React.memo(
  SignatureDraggableComponentBase,
);
